import classnames from "classnames";
import * as I from "immutable";
import moment from "moment-timezone";
import Raven from "raven-js";
import React, {Component} from "react";
import PropTypes from "prop-types";
import {Autosuggest} from "@ergeon/erg-components-react";

import {
  DESTINATION_TYPE,
  LUNCH_TITLE,
  SATURDAY_TAGS_CATEGORY,
  SATURDAY_TAGS_REASON,
  SERVICE_TYPE,
} from "config/constants";
import {trackEvent} from "metrics";
import {getDateFormat, formatDuration, isDesktop} from "utils";
import api from "v2/api";
import {canEditDuration} from "v2/api/Destinations";
import {apiError} from "v2/api/Errors";
import {apiHouse} from "v2/api/Houses";
import {apiOrder} from "v2/api/Orders";
import {AppData, Route} from "v2/api/Records";
import FieldError from "v2/components/FieldError";
import FieldGroup from "v2/components/FieldGroup";
import ServerError from "v2/components/ServerError";
import ModalForm from "v2/components/responsive/ModalForm";

import settings from "config/settings";

export default class AddDestinationForm extends Component {
  static contextTypes = {
    appData: PropTypes.instanceOf(AppData),
    location: PropTypes.object,
    onUpdateRouteData: PropTypes.func,
    params: PropTypes.shape({
      date: PropTypes.string.isRequired,
      routeId: PropTypes.string.isRequired,
    }),
    router: PropTypes.object,
    routeData: PropTypes.instanceOf(Route),
  }
  static defaultProps = {
    headerClassName: "erg-no-margin erg-margin-bottom-5",
    helpTextClassName: "help-text erg-no-margin-bottom",
    wellClassName: "well erg-no-margin erg-margin-top-15",
  }
  static propTypes = {
    headerClassName: PropTypes.string.isRequired,
    helpTextClassName: PropTypes.string.isRequired,
    modal: PropTypes.bool,
    onCancel: PropTypes.func,
    wellClassName: PropTypes.string.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    const {
      appData: {destinationTypeEditable},
    } = context;

    this.state = {
      address: null,
      category: null,
      dataError: null,
      duration: moment.duration(1, "hours"),
      errors: I.Map(),
      isRevisit: false,
      house: null,
      paid: null,
      reason: null,
      title: null,
      type: destinationTypeEditable.keySeq().first(),
    };
  }

  componentWillReceiveProps() {
    this.autosuggest && this.autosuggest.handleClear();
  }

  handleAddressChange = (evt) => {
    this.setState({
      address: evt.target.value,
    });
  }

  handleDurationChange = (evt) => {
    this.setState({
      duration: moment.duration(parseInt(evt.target.value, 10), "minutes"),
    });
  }

  handleHouseChange = (value) => {
    this.setState({
      address: null,
      house: value,
      title: null,
    });
  }

  handleOrderChange = (order) => {
    this.handleHouseChange(order.house);
    this.setState({order});
  }

  handleOrderVisitChange = (e) => {
    this.setState({orderVisit: e.target.value});
  }

  handleOrderVisitTemplateChange = (e) => {
    this.setState({orderVisitTemplate: e.target.value});
  }

  handleIsRevisitChange = () => {
    this.setState(({isRevisit}) => ({
      isRevisit: !isRevisit,
    }));
  }

  handleIsQuoteVisitChange = () => {
    this.setState(({isQuoteVisit}) => ({
      isQuoteVisit: !isQuoteVisit,
    }));
  }

  handleSubmit = (evt) => {
    evt.preventDefault();
    this.setState({
      submitted: true,
    });

    const {
      appData: {destinationTypeEditable},
      location: {query},
      onUpdateRouteData,
      params: {date, routeId},
      router,
    } = this.context;
    const {modal, onCancel} = this.props;
    const {
      address, duration, house, order,
      isRevisit, isQuoteVisit,
      title, type, orderVisit, orderVisitTemplate,
    } = this.state;

    const additionalData = {};
    if (canEditDuration({type, house})) {
      additionalData.duration = formatDuration(duration);
    }
    if (house) {
      additionalData.house = house.id;
    }
    if (order) {
      if (orderVisit) {
        additionalData["order_visit_id"] = orderVisit;
      } else {
        additionalData["order_visit"] = {
          "order": order.id,
          "is_quote_visit": isQuoteVisit,
          "order_visit_template": orderVisitTemplate,
        };
      }
    }

    api()
      .post(this.apiUrl, {
        "is_revisit": isRevisit,
        "raw_address": address,
        "raw_title": title,
        type,
        ...additionalData,
      }, {
        validateStatus: (status) => status === 201,
      })
      .then(({data}) => {
        onUpdateRouteData(null, () => {
          if (modal) {
            onCancel();
          }

          const mpEventData = {
            Address: address,
            ID: data.id,
            Date: date,
            Duration: additionalData.duration && additionalData.duration,
            "House ID": additionalData.house && additionalData.house,
            "Route ID": parseInt(routeId, 10),
            Title: title,
            Type: destinationTypeEditable.get(type),
          };
          trackEvent(`Add ${destinationTypeEditable.get(type)}`, [
            "Destination", `add-${type}`, data.id,
          ], mpEventData);

          if (isRevisit) {
            trackEvent(
              "Add Revisit",
              ["Destination", "add-revisit", data.id],
              mpEventData);
          }

          router.push({
            pathname: `/${date}/${routeId}/${data.id}`,
            query,
          });
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            errors: I.Map(err.response.data),
            submitted: false,
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  handleCategoryChange = (evt) => {
    this.setState({category: evt.target.value});
  }

  handlePaidChange = (evt) => {
    this.setState({paid: evt.target.value});
  }

  handleReasonChange = (evt) => {
    this.setState({reason: evt.target.value});
  }

  handleTitleChange = (evt) => {
    this.setState({
      title: evt.target.value,
    });
  }

  handleTypeChange = (evt) => {
    const type = evt.target.value;

    const additionalData = {};
    if (type === DESTINATION_TYPE.lunch) {
      additionalData.title = LUNCH_TITLE;
    } else if (this.state.title === LUNCH_TITLE) {
      additionalData.title = null;
    }

    this.setState({
      ...additionalData,
      type,
    });
  }

  handleSuggestionSelected = (evt, {suggestion}) => {
    this.handleHouseChange(suggestion);
  }

  handleOrderSuggestionSelected = (evt, {suggestion}) => {
    this.handleOrderChange(suggestion);
  }

  handleOnClearSuggestion = () => {
    this.setState({
      house: null,
      order: null,
    });
  }

  getSuggestionValue = (suggestion) => {
    return `${suggestion.customer.fullName}, ${suggestion.address}, ${suggestion.city}`;
  }

  getOrderSuggestionValue = (suggestion) => {
    const {id, house} = suggestion;
    return `${house.customer.fullName}, ${house.address}, ${house.city} (Order ID: #${id})`;
  }

  get apiUrl() {
    const {params: {routeId}} = this.context;
    return `/routes/${routeId}/destinations`;
  }

  get apiAutosuggestUrl() {
    const {params: {routeId}} = this.context;
    return `/routes/${routeId}/destinations/search-house`;
  }

  get apiOrderAutosuggestUrl() {
    const {params: {routeId}} = this.context;
    return `/routes/${routeId}/destinations/search-order`;
  }

  get isSaturdayMaintenanceHouse() {
    const {params: {date}, routeData} = this.context;
    const {type} = this.state;
    return (
      type === DESTINATION_TYPE.house &&
      routeData.type === SERVICE_TYPE.maintenance &&
      moment(date).day() === 6
    );
  }

  hasNewVisits(order) {
    return order && order.has("newVisits") && !order.get("newVisits").isEmpty();
  }

  hasOrderVisitTemplates(order) {
    return order && order.has("orderVisitTemplates") && !order.get("orderVisitTemplates").isEmpty();
  }

  renderContent() {
    const {
      appData: {destinationTypeEditable},
    } = this.context;
    const {helpTextClassName} = this.props;
    const {dataError, duration, errors, house, type} = this.state;

    if (dataError) {
      return <ServerError data={dataError} />;
    }

    let content;
    if (type === DESTINATION_TYPE.house) {
      content = this.renderHouseFields();
    } else if (type === DESTINATION_TYPE.meeting) {
      content = this.renderMeetingFields();
    } else if (type === DESTINATION_TYPE.lunch) {
      content = this.renderLunchFields();
    } else {
      content = this.renderOtherFields();
    }

    let durationContent;
    if (canEditDuration({type, house})) {
      const durationClassName = classnames({
        "pull-right": isDesktop(),
      });
      durationContent = (
        <div className={durationClassName}>
          <label
            className="control-label erg-padding-right-15"
            htmlFor="duration"
            style={{fontWeight: "normal"}}>
            Duration:
          </label>
          <input
            className="form-control erg-inline-block"
            id="duration"
            onChange={this.handleDurationChange}
            style={{width: "70px"}}
            type="number"
            value={duration.asMinutes()} /> min
        </div>
      );
    }

    let nonFieldErrors;
    if (errors.get("nonFieldErrors")) {
      nonFieldErrors = (
        <div className="form-inline has-error">
          <FieldError className={helpTextClassName}>
            {errors.get("nonFieldErrors")}
          </FieldError>
        </div>
      );
    }

    const typeClassName = classnames({
      "pull-left": isDesktop() && durationContent,
    });

    return (
      <div>
        <div className="form-inline clearfix">
          <div className={typeClassName}>
            <FieldGroup error={errors.get("type")}>
              <label
                className="control-label erg-margin-right-15"
                htmlFor="type"
                style={{fontWeight: "normal"}}>
                Destination Type:
              </label>
              <select
                className="form-control erg-inline-block"
                id="type"
                onChange={this.handleTypeChange}
                style={{width: "auto"}}
                value={type}>
                {destinationTypeEditable.map((value, key) => (
                  <option key={key} value={key}>{value}</option>
                )).toArray()}
              </select>
              <FieldError>{errors.get("type")}</FieldError>
            </FieldGroup>
          </div>
          {durationContent}
        </div>
        {content}
        {nonFieldErrors}
      </div>
    );
  }

  renderSuggestion = (suggestion) => {
    return (
      <div className="Search-Item">
        <ul className="Search-Item-PrimaryInfo list-unstyled">
          <li>
            <strong>{suggestion.customer.fullName}</strong> (ID: {suggestion.id})
          </li>
          <li>
            {suggestion.fullAddress}
          </li>
        </ul>
      </div>
    );
  }

  renderOrderSuggestion = (suggestion) => {
    return (
      <div className="Search-Item">
        <ul className="Search-Item-PrimaryInfo list-unstyled">
          <li>
            <strong>{suggestion.house.customer.fullName}</strong>
          </li>
          <li>
            {suggestion.productName} (Order ID: #{suggestion.id})
          </li>
          <li>
            Status: {suggestion.status}, Order date: {moment(suggestion.createdAt).format(getDateFormat())}
          </li>
          <li>
            {suggestion.house.address}
          </li>
        </ul>
      </div>
    );
  }

  renderHouseFields() {
    const {isRevisit} = this.state;

    return [
      this.renderHouseSelect(),
      this.renderHouseSaturdayFields(),
      <div className="erg-padding-left-20 erg-padding-top-5" key="is-revisit">
        <div className="checkbox">
          <label>
            <input
              onChange={this.handleIsRevisitChange}
              type="checkbox"
              value={isRevisit} />
            This is a <strong>revisit</strong>.
          </label>
          <small className="help-block">
            Later in UI destination title will be prepended
            with <i className="fa fa-history" /> icon.
          </small>
        </div>
      </div>,
    ];
  }

  renderHouseSelect() {
    const {routeData} = this.context;
    const {headerClassName, helpTextClassName, wellClassName} = this.props;
    const {order, orderVisit, orderVisitTemplate, errors, isQuoteVisit} = this.state;

    const captureException = (err) => {
      this.setState({
        dataError: apiError(err.response.data),
      });
      if (settings.LEVEL !== "production") {
        console.error(err);
      }
      Raven.captureException(err);
    };

    const isStore = routeData.type === SERVICE_TYPE.store;

    let content;
    if (isStore) {
      content = (
        <div key="order-content">
          <Autosuggest
            apiHandler={api}
            captureException={captureException}
            className="Search form-control"
            errorHandler={apiError}
            getSuggestionValue={this.getOrderSuggestionValue}
            itemHandler={apiOrder}
            onClear={this.handleOnClearSuggestion}
            onSuggestionSelected={this.handleOrderSuggestionSelected}
            placeholder="Start typing customer name or address or Order ID"
            ref={(ordersuggest) => this.ordersuggest = ordersuggest}
            renderSuggestion={this.renderOrderSuggestion}
            url={this.apiOrderAutosuggestUrl} />

          {(this.hasNewVisits(order) || this.hasOrderVisitTemplates(order)) && <div className="erg-margin-top-20">
            <h5 className={headerClassName}>Order visit</h5>
            <select
              className="form-control"
              onChange={this.handleOrderVisitChange}
              style={{width: "auto"}}
              value={orderVisit}>
              <option value="">New Visit</option>
              {order.get("newVisits").map((visit) => (
                <option key={visit.get("id")} value={visit.get("id")}>
                  {visit.get("name") || `#${visit.get("id")}`}
                </option>
              ))}
            </select>
          </div>}
          {!orderVisit && this.hasOrderVisitTemplates(order) &&
            <div className="erg-margin-top-20">
              <h5 className={headerClassName}>Order visit template</h5>
              <select
                className="form-control"
                onChange={this.handleOrderVisitTemplateChange}
                style={{width: "auto"}}
                value={orderVisitTemplate}>
                <option value="">Select a template</option>
                {order.get("orderVisitTemplates").map((template) => (
                  <option key={template.get("id")} value={template.get("id")}>
                    {template.get("name") || `#${template.get("id")}`}
                  </option>
                ))}
              </select>
            </div>}
          {(!this.hasNewVisits(order)) && <div className="checkbox">
            <label>
              <input
                onChange={this.handleIsQuoteVisitChange}
                type="checkbox"
                value={isQuoteVisit} />
              This is a "quote" visit.
            </label>
          </div>}
        </div>
      );
    } else {
      content = (
        <div key="order-content">
          <Autosuggest
            apiHandler={api}
            captureException={captureException}
            className="Search form-control"
            errorHandler={apiError}
            getSuggestionValue={this.getSuggestionValue}
            itemHandler={apiHouse}
            onClear={this.handleOnClearSuggestion}
            onSuggestionSelected={this.handleSuggestionSelected}
            placeholder="Start typing customer name or address"
            ref={(autosuggest) => this.autosuggest = autosuggest}
            renderSuggestion={this.renderSuggestion}
            url={this.apiAutosuggestUrl} />
        </div>
      );
    }

    return (
      <div className={wellClassName} key="house">
        <h5 className={headerClassName}>{isStore ? "Order" : "House"}</h5>
        <p className={helpTextClassName}>
          {isStore ? "Select Order" : "Address of an existing customer in our system."}
        </p>
        {content}
        <FieldError className={helpTextClassName}>
          {errors.get("house")}
        </FieldError>
      </div>
    );
  }

  renderHouseSaturdayFields() {
    const {headerClassName, helpTextClassName, wellClassName} = this.props;
    const {category, paid, reason} = this.state;

    // Only applicable to Saturday MAI routes
    if (!this.isSaturdayMaintenanceHouse) {
      return;
    }

    return [
      <div className={wellClassName} key="reason">
        <h5 className={headerClassName}>Reason</h5>
        <p className={helpTextClassName}>
          Why is this house being put on a Saturday route ?
        </p>
        <select
          className="form-control"
          onChange={this.handleReasonChange}
          style={{width: "auto"}}
          value={reason}>
          <option value="">-</option>
          {SATURDAY_TAGS_REASON.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
      </div>,
      <div className={wellClassName} key="paid">
        <h5 className={headerClassName}>Paid ?</h5>
        <label>
          <input
            checked={paid === "Paid"}
            name="paid"
            onChange={this.handlePaidChange}
            type="radio"
            value="Paid" />
          <span className="erg-margin-left-10">Yes</span>
        </label>
        <br />
        <label>
          <input
            checked={paid === "Unpaid"}
            name="paid"
            onChange={this.handlePaidChange}
            type="radio"
            value="Unpaid" />
          <span className="erg-margin-left-10">No</span>
        </label>
      </div>,
      <div className={wellClassName} key="category">
        <h5 className={headerClassName}>Category</h5>
        <select
          className="form-control"
          onChange={this.handleCategoryChange}
          style={{width: "auto"}}
          value={category}>
          <option value="">-</option>
          {SATURDAY_TAGS_CATEGORY.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
      </div>,
    ];
  }

  renderLunchFields() {
    const {headerClassName, helpTextClassName, wellClassName} = this.props;
    const {errors, title} = this.state;
    return (
      <div className={wellClassName}>
        <h5 className={headerClassName}>Title</h5>
        <input
          autoFocus={isDesktop()}
          className="form-control"
          onChange={this.handleTitleChange}
          type="text"
          value={title || ""} />
        <FieldError className={helpTextClassName}>
          {errors.get("rawTitle")}
        </FieldError>
      </div>
    );
  }

  renderMeetingFields() {
    const {headerClassName, helpTextClassName, wellClassName} = this.props;
    const {address, errors, title} = this.state;
    return [
      <div className={wellClassName} key="title">
        <h5 className={headerClassName}>Title</h5>
        <input
          autoFocus={isDesktop()}
          className="form-control"
          onChange={this.handleTitleChange}
          type="text"
          value={title || ""} />
        <p className={helpTextClassName}>
          e.g. <strong>Jack in the Box</strong>
        </p>
        <FieldError className={helpTextClassName}>
          {errors.get("rawTitle")}
        </FieldError>
      </div>,
      <div className={wellClassName} key="address">
        <h5 className={headerClassName}>Address</h5>
        <input
          className="form-control"
          onChange={this.handleAddressChange}
          type="text"
          value={address || ""} />
        <p className={helpTextClassName}>
          Address must be formatted as follows:<br />
          <strong>200 W El Camino Real, Mountain View, CA 94040</strong>
        </p>
        <FieldError className={helpTextClassName}>
          {errors.get("rawAddress")}
        </FieldError>
      </div>,
    ];
  }

  renderOtherFields() {
    const {headerClassName, helpTextClassName, wellClassName} = this.props;
    const {address, errors, title} = this.state;
    return [
      <div className={wellClassName} key="address">
        <h5 className={headerClassName}>Address</h5>
        <input
          autoFocus={isDesktop()}
          className="form-control"
          onChange={this.handleAddressChange}
          type="text"
          value={address || ""} />
        <p className={helpTextClassName}>
          e.g., <strong>260 Sheridan Ave, Palo Alto, CA 94306</strong>
        </p>
        <FieldError className={helpTextClassName}>
          {errors.get("rawAddress")}
        </FieldError>
      </div>,
      <div className={wellClassName} key="title">
        <h5 className={headerClassName}>Title</h5>
        <input
          className="form-control"
          onChange={this.handleTitleChange}
          type="text"
          value={title || ""} />
        <p className={helpTextClassName}>
          e.g. <strong>Please call your field supervisor</strong>
        </p>
        <FieldError className={helpTextClassName}>
          {errors.get("rawTitle")}
        </FieldError>
      </div>,
    ];
  }

  render() {
    const {paid, reason, submitted} = this.state;

    let disabled = false;
    // Reason and paid tags are required
    if (this.isSaturdayMaintenanceHouse) {
      disabled = !reason || !paid;
    }

    return (
      <ModalForm
        btnSubmitDisabled={submitted || disabled}
        btnSubmitLabel="Create Destination"
        modalBodyClassName="erg-padding-15"
        modalDialogClassName="ModalLarge"
        modalTitle="New Destination"
        onSubmit={this.handleSubmit}
        pageTitle="New Destination"
        {...this.props}>
        {this.renderContent()}
      </ModalForm>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/responsive/AddDestinationForm.js
 **/